import type {Nodes, Paragraph} from 'mdast'
import {useState} from 'react'
import {source} from 'unist-util-source'
import {SKIP, visit} from 'unist-util-visit'

import type {CopilotMarkdownExtension, ReactComponentsExtension, RehypeHtmlTransformer} from '../extension'
import {dataAttrToPropName} from '../utils'
import {isTrustedMediaSource} from './trusted-media-domains'

// Read the video label off the kebab-case prop react-markdown exposes, but set the camelCase hast property name
// (matching the suggestions / review-comments extensions) so hast converts it to the correct DOM attribute.
const VIDEO_LABEL_ATTRIBUTE = 'data-video-label'
const VIDEO_LABEL_PROPERTY = dataAttrToPropName(VIDEO_LABEL_ATTRIBUTE)

/** Recursively extract the plain-text content of an mdast node (drops formatting like backticks). */
function mdastToText(node: Nodes): string {
  if ('value' in node && typeof node.value === 'string') return node.value
  if ('children' in node && Array.isArray(node.children)) return node.children.map(mdastToText).join('')
  return ''
}

/**
 * A paragraph is a video candidate when its only meaningful content is a single link, either a bare
 * autolinked URL (`https://.../video.mp4`) or a markdown link (`[label](https://.../video.mp4)`). This mirrors
 * how attachment videos are authored in issue/PR bodies: the URL sits alone on its own line.
 */
function soleLinkChild(paragraph: Paragraph) {
  const meaningfulChildren = paragraph.children.filter(child => child.type !== 'text' || child.value.trim() !== '')
  const [onlyChild] = meaningfulChildren
  if (meaningfulChildren.length !== 1 || onlyChild?.type !== 'link') return undefined
  return onlyChild
}

/**
 * Rewrites bare-URL / single-link paragraphs into `video` hast nodes. We can't know from the URL alone whether
 * it actually points at a video, so the `video` React component speculatively renders it and falls back to a
 * plain link if the browser can't load it (see `VideoTag` below).
 */
function tagVideoCandidates(tree: Nodes): void {
  visit(tree, 'paragraph', (node, index, parent) => {
    if (!parent || typeof index !== 'number') return
    const link = soleLinkChild(node)
    // Only allow-listed media URLs become video candidates; anything else stays a regular link.
    if (!link || !isTrustedMediaSource(link.url)) return

    const label = mdastToText(link).trim()

    node.data = {
      ...node.data,
      hName: 'video',
      hProperties: {
        src: link.url,
        controls: true,
        playsInline: true,
        preload: 'metadata',
        [VIDEO_LABEL_PROPERTY]: label || link.url,
      },
      hChildren: [],
    }
  })
}

/** Reverts any `video` node whose `src` isn't from an explicitly allow-listed domain back to a plain link. */
const ensureTrustedSource: RehypeHtmlTransformer = (tree, file) =>
  visit(tree, (node, index, parent) => {
    if (node.type !== 'element' || node.tagName !== 'video') return
    if (!('src' in node.properties)) return
    const src = node.properties.src as string
    if (isTrustedMediaSource(src)) return

    const md = source(file, node) ?? src
    const spanNode = {
      type: 'element' as const,
      tagName: 'span',
      properties: {},
      children: [{type: 'text' as const, value: md}],
    }
    if (parent && typeof index === 'number') {
      parent.children[index] = spanNode
      return [SKIP, index]
    }
  })

/**
 * Renders a candidate video URL as a real `<video>` element. Since there's no way to know ahead of time
 * whether the URL actually points at a playable video, this speculatively renders the video and falls back to
 * a plain link if the browser fails to load it (invalid URL, non-video file, expired attachment link, etc).
 */
function VideoTag({src, label, openLinksInCurrentTab}: {src: string; label: string; openLinksInCurrentTab?: boolean}) {
  const [failed, setFailed] = useState(false)

  if (failed) {
    const linkTargetProps = openLinksInCurrentTab ? {} : {target: '_blank', rel: 'noopener noreferrer'}
    return (
      <a href={src} {...linkTargetProps}>
        {label}
      </a>
    )
  }

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- attachment videos rarely have caption tracks available
    <video src={src} controls playsInline preload="metadata" aria-label={label} onError={() => setFailed(true)} />
  )
}

const buildReactComponents = ({openLinksInCurrentTab}: VideosExtensionOptions): ReactComponentsExtension => ({
  video: props => {
    const src = typeof props.src === 'string' ? props.src : undefined
    const labelProp = props[VIDEO_LABEL_ATTRIBUTE as 'data-video-label']
    const label = typeof labelProp === 'string' ? labelProp : src

    if (!src || !label) return props.children

    // Keying by `src` remounts the video when a streaming URL changes, so a failure from a partial URL doesn't
    // permanently downgrade the completed one to a link.
    return <VideoTag key={src} src={src} label={label} openLinksInCurrentTab={openLinksInCurrentTab} />
  },
})

interface VideosExtensionOptions {
  openLinksInCurrentTab?: boolean
}

export default function videosExtension(options: VideosExtensionOptions = {}): CopilotMarkdownExtension {
  return {
    transformMarkdown: tagVideoCandidates,
    transformHtml: ensureTrustedSource,
    reactComponents: buildReactComponents(options),
  }
}
